import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import logoConnect10 from '../assets/logo-connect10.png';

/**
 * @file Gera o PDF de "Exportar Projeto" do Mapa de Cobertura: imagem do mapa (modo claro),
 * detalhamento do endereço/CTO/metragem de drop e um QR Code apontando para a CTO no Google Maps.
 */

// Carrega uma imagem e retorna o data URL junto das dimensões naturais,
// para que o PDF sempre respeite a proporção original (evita esticar a imagem).
const imageToDataUrlWithSize = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = src;
});

// Ajusta uma imagem dentro de uma caixa (maxWidth x maxHeight) preservando a proporção original (contain).
const fitWithinBox = (naturalWidth, naturalHeight, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
    return { width: naturalWidth * ratio, height: naturalHeight * ratio };
};

/**
 * @param {Object} params
 * @param {string} params.mapImageDataUrl - PNG data URL do screenshot do mapa (html2canvas).
 * @param {number} params.mapImageWidth - Largura (px) do canvas capturado, para preservar a proporção.
 * @param {number} params.mapImageHeight - Altura (px) do canvas capturado, para preservar a proporção.
 * @param {string} params.address - Endereço do cliente já formatado.
 * @param {{lat:number,lng:number}} params.clientCoords - Coordenadas do cliente.
 * @param {{name:string, coords:{lat:number,lng:number}}} params.cto - Caixa (CTO) selecionada.
 * @param {number} params.dropMeters - Metragem de drop necessária (sem a margem de 10%).
 * @param {string} params.exportedBy - Nome do usuário que gerou o PDF.
 */
export const exportCoverageProjectPdf = async ({ mapImageDataUrl, mapImageWidth, mapImageHeight, address, clientCoords, cto, dropMeters, exportedBy }) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 12;
    let cursorY = margin;

    // Cabeçalho com logo (proporção original preservada, altura máx. de 12mm)
    try {
        const logo = await imageToDataUrlWithSize(logoConnect10);
        const logoBox = fitWithinBox(logo.width, logo.height, 34, 12);
        doc.addImage(logo.dataUrl, 'PNG', margin, cursorY, logoBox.width, logoBox.height, undefined, 'FAST');
    } catch (err) {
        console.error('Falha ao carregar logo para o PDF:', err);
    }

    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'bold');
    doc.text('Projeto de Instalação', pageWidth - margin, cursorY + 6, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, pageWidth - margin, cursorY + 11, { align: 'right' });
    if (exportedBy) {
        doc.text(`Por: ${exportedBy}`, pageWidth - margin, cursorY + 16, { align: 'right' });
    }

    cursorY += 22;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 8;

    // Imagem do mapa (proporção original do canvas preservada, dentro de uma caixa máxima)
    if (mapImageDataUrl && mapImageWidth && mapImageHeight) {
        const maxWidth = pageWidth - margin * 2;
        const maxHeight = 110;
        const box = fitWithinBox(mapImageWidth, mapImageHeight, maxWidth, maxHeight);
        const imgX = margin + (maxWidth - box.width) / 2;
        doc.setDrawColor(203, 213, 225);
        doc.rect(imgX, cursorY, box.width, box.height);
        doc.addImage(mapImageDataUrl, 'PNG', imgX, cursorY, box.width, box.height, undefined, 'FAST');
        cursorY += box.height + 10;
    }

    // Detalhamento em texto
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('Detalhamento do Projeto', margin, cursorY);
    cursorY += 8;

    // Layout empilhado (label em uma linha, valor na linha seguinte) para nunca
    // sobrepor texto independentemente do tamanho do rótulo.
    // `wrapWidth` permite reservar espaço à direita (ex.: para o QR Code) quando necessário.
    const addField = (label, value, wrapWidth = pageWidth - margin * 2) => {
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(100, 116, 139);
        doc.text(label.toUpperCase(), margin, cursorY);
        cursorY += 5;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(11);
        doc.setTextColor(30, 41, 59);
        const wrapped = doc.splitTextToSize(value || 'N/A', wrapWidth);
        doc.text(wrapped, margin, cursorY);
        cursorY += wrapped.length * 5.5 + 5;
    };

    addField('Endereço do Cliente', address);
    addField('Coordenadas do Cliente', clientCoords ? `${clientCoords.lat.toFixed(6)}, ${clientCoords.lng.toFixed(6)}` : 'N/A');

    // QR Code apontando para a CTO no Google Maps, posicionado junto ao nome/geolocalização da CTO
    const qrSize = 26;
    const qrAnchorY = cursorY;
    const textWrapWidth = pageWidth - margin * 2 - (qrSize + 8);
    addField('Caixa Selecionada', cto?.name, textWrapWidth);
    addField('Geolocalização da CTO', cto?.coords ? `${cto.coords.lat.toFixed(6)}, ${cto.coords.lng.toFixed(6)}` : 'N/A', textWrapWidth);

    if (cto?.coords) {
        try {
            const mapsUrl = `https://www.google.com/maps?q=${cto.coords.lat},${cto.coords.lng}`;
            const qrDataUrl = await QRCode.toDataURL(mapsUrl, { margin: 1, width: 200 });
            const qrX = pageWidth - margin - qrSize;
            doc.addImage(qrDataUrl, 'PNG', qrX, qrAnchorY, qrSize, qrSize);
            doc.setFontSize(6.5);
            doc.setTextColor(100, 116, 139);
            doc.text('Abrir CTO no Google Maps', qrX + qrSize / 2, qrAnchorY + qrSize + 4, { align: 'center' });
            cursorY = Math.max(cursorY, qrAnchorY + qrSize + 8);
        } catch (err) {
            console.error('Falha ao gerar QR Code:', err);
        }
    }

    addField('Metragem de Drop Necessária', dropMeters != null ? `${Math.round(dropMeters)} m` : 'N/A');
    addField('Metragem de Drop + 10% (margem)', dropMeters != null ? `${Math.round(dropMeters * 1.1)} m` : 'N/A');

    const fileSafeAddress = (address || 'projeto').replace(/[^a-z0-9]+/gi, '_').slice(0, 60);
    doc.save(`projeto_${fileSafeAddress}.pdf`);
};

export default exportCoverageProjectPdf;
